import React, { FC, useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { Trans, useTranslation } from 'react-i18next'
import jwtDecode from 'jwt-decode'
import { BtButton } from '@linqpal/components/src/ui'
import { urls } from '@linqpal/models/src/dictionaries'
import { externalUrlOpener } from '../../utils/helpers'
import LegalFiles from '../../utils/helpers/legalFiles'
import { useResponsive } from '../../utils/hooks'
import { AgreementSubmittedMessage } from './AgreementSubmittedMessage'
import { styles } from './styles'
import { ApprovalErrorMessage, ErrorCode } from './ApprovalErrorMessage'
import { routes } from '@linqpal/models'
import { v4 } from 'public-ip'
import { Spinner } from '@ui-kitten/components'
import { Spacer } from '../../ui/atoms'
import {
  IApprovalInfo,
  IDownloadable,
} from '@linqpal/models/src/types/approvals'
import { ApprovalPageContainer } from './ApprovalPageContainer'
import PdfViewer from '../../ui/organisms/PdfViewer'

export const CreditAgreementsApproval: FC<any> = ({ route }) => {
  const { t } = useTranslation('global')
  const { sm } = useResponsive()

  const [approvalInfo, setApprovalInfo] = useState<IApprovalInfo | null>()

  const [masterAgreementLink, setMasterAgreementLink] =
    useState<IDownloadable | null>()
  const [guarantorAgreementLink, setGuarantorAgreementLink] =
    useState<IDownloadable | null>()

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [approved, setApproved] = useState(false)
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null)

  const jwt = route?.params?.token

  useEffect(() => {
    if (jwt) {
      try {
        const decodedToken = jwtDecode<IApprovalInfo>(jwt)
        if (!decodedToken.documentId || !decodedToken.approvalId) {
          setErrorCode(ErrorCode.UnhandledError)
        } else {
          setApprovalInfo({ ...decodedToken })
          setLoading(true)

          Promise.all([
            routes.approvals.downloadMasterAgreement(
              decodedToken.documentId,
              jwt,
            ),
            routes.approvals.downloadPersonalGuarantorAgreement(
              decodedToken.ownerId,
              jwt,
            ),
          ])
            .then(([masterAgreementResponse, guarantorAgreementResponse]) => {
              setMasterAgreementLink(masterAgreementResponse)
              setGuarantorAgreementLink(guarantorAgreementResponse)
            })
            .catch(() => setErrorCode(ErrorCode.UnhandledError))
            .finally(() => setLoading(false))
        }
      } catch {
        setErrorCode(ErrorCode.UnhandledError)
      }
    } else {
      setErrorCode(ErrorCode.UnhandledError)
    }
  }, [jwt])

  const onSubmit = async () => {
    try {
      setSubmitting(true)

      const ipAddress = await v4()
      await routes.approvals.approveCreditAgreements(
        route?.params?.token,
        ipAddress,
      )

      setApproved(true)
    } catch (e: any) {
      if (e.code === ErrorCode.AlreadyApproved) {
        setApproved(true)
      } else {
        setErrorCode(e.code)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ApprovalPageContainer title={t('DocumentApproval.credit.header')}>
      {errorCode ? (
        <ApprovalErrorMessage errorCode={errorCode} />
      ) : !approved ? (
        <View style={{ alignItems: 'center' }}>
          <Spacer height={35} />
          {masterAgreementLink?.url ? (
            <PdfViewer pdfUrl={masterAgreementLink.url} />
          ) : (
            <View
              style={{
                ...localStyles.docViewer,
                width: sm ? '100%' : 350,
              }}
            />
          )}
          <Spacer height={35} />
          <Text style={styles.title}>
            <Trans
              t={t}
              i18nKey={'DocumentApproval.credit.title'}
              values={{ company: approvalInfo?.companyName ?? '...' }}
            />
          </Text>
          {!loading ? (
            <Text style={styles.message}>
              <Trans
                t={t}
                i18nKey={
                  guarantorAgreementLink?.url
                    ? 'DocumentApproval.credit.individualOwnerMessage'
                    : 'DocumentApproval.credit.entityOwnerMessage'
                }
                components={{
                  tradeCredit: (
                    <Text
                      style={styles.link}
                      onPress={() => {
                        if (approvalInfo) {
                          // since S3 link can expire before click, request a new one each time
                          LegalFiles.downloadMasterAgreementWithJwt(
                            approvalInfo.documentId,
                            jwt,
                          )
                        }
                      }}
                    />
                  ),
                  pga: (
                    <Text
                      style={styles.link}
                      onPress={() => {
                        if (approvalInfo?.ownerId) {
                          LegalFiles.downloadPersonalGuarantorAgreementWithJwt(
                            approvalInfo.ownerId,
                            jwt,
                          )
                        }
                      }}
                    />
                  ),
                  customer: (
                    <Text
                      style={styles.link}
                      onPress={LegalFiles.downloadCustomerAgreement}
                    />
                  ),
                  terms: (
                    <Text
                      style={styles.link}
                      onPress={() => externalUrlOpener(urls.TC)}
                    />
                  ),
                  privacy: (
                    <Text
                      style={styles.link}
                      onPress={() => externalUrlOpener(urls.PP)}
                    />
                  ),
                }}
              />
            </Text>
          ) : (
            <View style={localStyles.spinner}>
              <Spinner />
            </View>
          )}
          <BtButton
            size={'large'}
            style={styles.button}
            disabled={submitting || loading}
            onPress={onSubmit}
          >
            {t('DocumentApproval.submit')}
          </BtButton>
        </View>
      ) : (
        <AgreementSubmittedMessage />
      )}
    </ApprovalPageContainer>
  )
}

const localStyles = {
  docViewer: {
    height: 400,
    backgroundColor: 'white',
    marginTop: -50,
    padding: 15,
    border: '20px solid #f8f9f9',
    borderRadius: 20,
    borderBottom: 0,
  },
  spinner: {
    marginTop: 25,
  },
}
